import { Info, Lightbulb, Sparkles, HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface InfoTooltipProps {
  content: string;
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
}

export function InfoTooltip({ content, className, side = "top" }: InfoTooltipProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center justify-center h-4 w-4 rounded-full text-muted-foreground hover:text-foreground transition-colors",
            className
          )}
          aria-label="More information"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side={side} className="max-w-xs">
        <p className="text-sm">{content}</p>
      </TooltipContent>
    </Tooltip>
  );
}

type FeatureTipVariant = "info" | "tip" | "new" | "help";

interface FeatureTipProps {
  title?: string;
  content: string;
  tip?: string;
  variant?: FeatureTipVariant;
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
}

export function FeatureTip({ 
  title, 
  content, 
  tip, 
  variant = "info", 
  className, 
  side = "top" 
}: FeatureTipProps) {
  const variantConfig = {
    info: {
      icon: Info,
      iconColor: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    tip: {
      icon: Lightbulb,
      iconColor: "text-amber-500",
      bgColor: "bg-amber-500/10",
    },
    new: {
      icon: Sparkles,
      iconColor: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
    help: {
      icon: HelpCircle,
      iconColor: "text-muted-foreground",
      bgColor: "bg-muted",
    },
  };

  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center justify-center h-5 w-5 rounded-full transition-colors",
            config.bgColor,
            config.iconColor,
            "hover:opacity-80",
            className
          )}
          aria-label="Feature information"
          data-testid={`feature-tip-${variant}`}
        >
          <Icon className="h-3 w-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent 
        side={side} 
        className="max-w-xs p-3"
      >
        <div className="space-y-1.5">
          {title && (
            <div className="flex items-center gap-1.5">
              <Icon className={cn("h-3.5 w-3.5", config.iconColor)} />
              <span className="font-medium text-sm">{title}</span>
            </div>
          )}
          <p className="text-sm text-muted-foreground">{content}</p>
          {tip && (
            <div className="flex items-start gap-1.5 pt-1.5 border-t border-border/50">
              <Lightbulb className="h-3 w-3 text-amber-500 mt-0.5 shrink-0" />
              <p className="text-xs text-muted-foreground italic">{tip}</p>
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

interface OnboardingTipProps {
  featureKey: string;
  title?: string;
  description?: string;
  tryThis?: string;
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
}

const ONBOARDING_TIPS: Record<string, { title: string; description: string; tryThis?: string }> = {
  chat: {
    title: "Smart Chat",
    description: "Have natural conversations with AI. Your chats are saved and organized automatically.",
    tryThis: "Ask a question about something you're curious about!",
  },
  spaces: {
    title: "Mastermind Spaces",
    description: "Create organized containers for your knowledge. Think of them as smart folders for different areas of your life.",
    tryThis: "Create your first space around a topic you want to master.",
  },
  library: {
    title: "Your Library",
    description: "All your notes, saved content, and imported documents in one place. Search across everything instantly.",
    tryThis: "Import a document or save a web article to get started.",
  },
  sources: {
    title: "Knowledge Sources",
    description: "Import content from URLs, YouTube, Google Drive, and more. AI can reference these when answering your questions.",
    tryThis: "Paste a URL to an article you found interesting.",
  },
  tools: {
    title: "AI Toolkit",
    description: "Powerful AI tools including prompts, agents, and workflows to supercharge your productivity.",
    tryThis: "Try a quick prompt to see AI in action.",
  },
  agents: {
    title: "AI Agents",
    description: "Custom AI personas with specific skills and knowledge. Create agents for different tasks.",
    tryThis: "Browse the agent library to see what's possible.",
  },
};

export function OnboardingTip({ 
  featureKey, 
  title, 
  description, 
  tryThis,
  className, 
  side = "right" 
}: OnboardingTipProps) {
  const predefinedTip = ONBOARDING_TIPS[featureKey];
  const finalTitle = title || predefinedTip?.title || "Feature";
  const finalDescription = description || predefinedTip?.description || "";
  const finalTryThis = tryThis || predefinedTip?.tryThis;

  return (
    <FeatureTip
      title={finalTitle}
      content={finalDescription}
      tip={finalTryThis}
      variant="tip"
      className={className}
      side={side}
    />
  );
}
